import { isEmpty } from "lodash";

/**
 * Check if an objective can have children based on childrenMap
 * @param {String} objectiveId - The objective ID
 * @param {Object} childrenMap - Map of objective IDs to their children arrays
 * @returns {boolean|null} true if has children, false if no children, null if unknown
 */
export const canHaveChildren = (objectiveId, childrenMap) => {
  if (!childrenMap || !childrenMap[objectiveId]) {
    return null;
  }

  const children = childrenMap[objectiveId];

  if (children === undefined) {
    return null;
  }

  return children && children.length > 0;
};

/**
 * Check if node should show expand toggle
 * @param {String} objectiveId - The objective ID
 * @param {Object} childrenMap - Map of objective IDs to their children arrays
 * @returns {boolean} true if should show expand toggle
 */
export const shouldShowExpand = (objectiveId, childrenMap) => {
  const hasChildren = canHaveChildren(objectiveId, childrenMap);
  return hasChildren !== false;
};

/**
 * Create a tree node from an objective
 * @param {Object} objective - The objective object
 * @param {Object} childrenMap - Map of objective IDs to their children arrays
 * @param {Object} parentNode - The parent node (optional)
 * @returns {Object} Tree node
 */
export const createObjectiveTreeNode = (
  objective,
  childrenMap = {},
  parentNode = null
) => {
  const { id, name } = objective;
  const hasChildren = canHaveChildren(id, childrenMap);

  return {
    id,
    name,
    objective,
    active: false,
    toggled: false,
    isParentType: hasChildren !== false,
    loading: false,
    children: hasChildren === false ? [] : null,
    parentNode,
  };
};

/**
 * Build childrenMap from objectives array
 * @param {Array} objectives - Array of objective objects
 * @returns {Object} Map of objective ID to children arrays
 */
export const buildChildrenMap = (objectives) => {
  const map = {};

  if (!objectives || objectives.length === 0) {
    return map;
  }

  objectives.forEach((objective) => {
    map[objective.id] = [];
  });

  return map;
};

/**
 * Build root level tree from objectives
 * @param {Array} objectives - Array of objective objects
 * @param {Object} childrenMap - Map of objective IDs to their children arrays
 * @returns {Array} Tree data array
 */
export const buildObjectivesTree = (objectives, childrenMap = {}) => {
  if (isEmpty(objectives)) {
    return [];
  }

  return objectives.map((objective) =>
    createObjectiveTreeNode(objective, childrenMap)
  );
};

/**
 * Add child objectives to a parent node
 * @param {Object} parentNode - The parent tree node
 * @param {Array} childObjectives - Array of child objective objects
 * @param {Object} childrenMap - Map of objective IDs to their children arrays
 */
export const addChildObjectivesToNode = (
  parentNode,
  childObjectives,
  childrenMap = {}
) => {
  if (!parentNode) {
    return;
  }

  if (isEmpty(childObjectives)) {
    Object.assign(parentNode, {
      children: [
        {
          noActivation: true,
          isPlaceholder: true,
          name: "No child objectives",
          objective: {},
        },
      ],
      isParentType: false,
      loading: false,
    });
    return;
  }

  Object.assign(parentNode, {
    children: childObjectives.map((objective) =>
      createObjectiveTreeNode(objective, childrenMap, parentNode)
    ),
    isParentType: true,
    loading: false,
  });
};

/**
 * Find a node in the tree by ID
 * @param {Array|Object} tree - The tree data
 * @param {String} nodeId - The node ID to find
 * @returns {Object|null} The found node or null
 */
export const findNodeById = (tree, nodeId) => {
  if (!tree) {
    return null;
  }

  const searchArray = Array.isArray(tree) ? tree : [tree];

  for (let i = 0; i < searchArray.length; i += 1) {
    const node = searchArray[i];
    if (node.id === nodeId) {
      return node;
    }

    if (node.children && Array.isArray(node.children)) {
      const found = findNodeById(node.children, nodeId);
      if (found) {
        return found;
      }
    }
  }

  return null;
};

/**
 * Find all nodes in the tree with the same objective ID
 * @param {Array|Object} tree - The tree data
 * @param {String} nodeId - The node ID to find
 * @returns {Array} Array of all nodes with this ID
 */
export const findAllNodesById = (tree, nodeId) => {
  if (!tree) {
    return [];
  }

  const results = [];
  const searchArray = Array.isArray(tree) ? tree : [tree];

  const search = (nodes) => {
    nodes.forEach((node) => {
      if (node.id === nodeId) {
        results.push(node);
      }

      if (node.children && Array.isArray(node.children)) {
        search(node.children);
      }
    });
  };

  search(searchArray);
  return results;
};

/**
 * Recursively builds a childrenMap for objectives and their descendants.
 * NOTE: This mutates the childrenMap parameter.
 * @param {Array} objectives - Flat array of objective objects
 * @param {Object} childrenMap - Map to populate (mutated)
 * @param {String} parentObjId - ID of the parent objective
 */
export const buildChildrenMapRecursively = (
  objectives,
  childrenMap,
  parentObjId
) => {
  const children =
    objectives?.filter((obj) => obj?.parentIds?.includes(parentObjId)) || [];

  // eslint-disable-next-line no-param-reassign
  childrenMap[parentObjId] = children;

  // Recursively process children
  children.forEach((child) => {
    if (!childrenMap[child.id]) {
      buildChildrenMapRecursively(objectives, childrenMap, child.id);
    }
  });
};

/**
 * Build hierarchical structure from flat array of objectives
 * Handles multi-level nesting by filtering children at each level
 * @param {Array} objectives - Flat array of objective objects
 * @param {String} parentId - ID of the parent (null for root level)
 * @returns {Object} { directChildren, childrenMap }
 */
export const buildHierarchicalChildrenMap = (objectives, parentId = null) => {
  if (!objectives || objectives.length === 0) {
    return { directChildren: [], childrenMap: {} };
  }

  // Find direct children of the parent
  const directChildren = objectives.filter((obj) => {
    if (parentId === null) {
      // Root level: objectives with no parents or empty parentIds
      return !obj.parentIds || obj.parentIds.length === 0;
    }
    // Child level: objectives that include parentId in their parentIds
    return obj.parentIds && obj.parentIds.includes(parentId);
  });

  const childrenMap = {};

  // Process all direct children and their descendants
  directChildren.forEach((child) => {
    buildChildrenMapRecursively(objectives, childrenMap, child.id);
  });

  // Also mark any objectives not yet in the map
  objectives.forEach((obj) => {
    if (!childrenMap[obj.id]) {
      childrenMap[obj.id] = [];
    }
  });

  return { directChildren, childrenMap };
};

/**
 * Recursively add children to a node and all its descendants
 * @param {Object} node - The tree node to add children to
 * @param {Object} childrenMap - Map of objective IDs to their children arrays
 */
export const addNestedChildrenToNode = (node, childrenMap) => {
  if (!node || node.isPlaceholder) {
    return;
  }

  const nodeChildren = childrenMap[node.id] || [];

  if (nodeChildren.length > 0) {
    // Add children to this node
    addChildObjectivesToNode(node, nodeChildren, childrenMap);

    // Recursively add grandchildren to each child
    if (node.children && Array.isArray(node.children)) {
      node.children.forEach((childNode) => {
        if (!childNode.isPlaceholder) {
          addNestedChildrenToNode(childNode, childrenMap);
        }
      });
    }
  } else {
    // Add placeholder if no children
    Object.assign(node, {
      children: [{ noActivation: true, isPlaceholder: true, name: "" }],
      loading: false,
    });
  }
};
